import Choices from "choices.js"
import { nanoid } from "nanoid"

import Hook from "../utils/hook"

export default Hook(`FormChoices`, {
  actions: [
    {
      name: `valueChange`,
      type: `choice`,
    },
    {
      name: `valueChange`,
      type: `change`,
    },
    {
      name: `search`,
      type: `search`,
    },
  ],
  choices: null,
  value: ``,
  createButton: null,
  createConfig: null,
  searchValue: ``,
  inputElement: null,
  inputKeydown: null,

  mounted() {
    this.initChoices()
  },

  beforeUpdate() {
    this.destroyChoices()
  },

  updated() {
    this.initChoices()
  },

  destroyed() {
    this.destroyChoices()
  },

  initChoices() {
    const createEvent = this.el.dataset.createEvent
    const createText = this.el.dataset.createText
    const createParams = this.el.dataset.createParams
      ? JSON.parse(this.el.dataset.createParams)
      : {}

    let tagsDomId = null

    const opts = {
      shouldSort: false,
      removeItemButton: true,
      allowHTML: true,
    }

    if (createEvent && createText) {
      tagsDomId = nanoid()
      opts.noResultsText = `<span id="${tagsDomId}"></span>`
      opts.fuseOptions = { threshold: 0 }
    }

    this.choices = new Choices(this.el, opts)

    // prevents triggering `phx-change` on phx forms when filtering options
    this.choices.input.element.setAttribute(`phx-debounce`, `blur`)

    if (createEvent && createText) {
      this.initCreateTag(createEvent, createText, createParams, tagsDomId)
    }

    if (this.value && !this.el.value) {
      this.choices.setValue(this.el.multiple ? this.value : [this.value])
    }
  },

  initCreateTag(createEvent, createText, createParams, tagsDomId) {
    this.createConfig = { createEvent, createText, createParams, tagsDomId }
    this.searchValue = ``

    // Allow Enter key to create a tag when no results are shown
    // (the input is created by Choices.js, so the listener is added manually
    // and removed in destroyChoices())
    const container = this.choices.containerOuter.element
    this.inputElement = this.choices.input.element
    this.inputKeydown = (e) => {
      if (e.key === `Enter` && this.createButton) {
        const notice = container.querySelector(`.choices__notice--no-results`)
        if (notice && this.searchValue) {
          e.preventDefault()
          e.stopPropagation()
          this.createTag(createEvent, this.searchValue, createParams)
        }
      }
    }
    this.inputElement.addEventListener(`keydown`, this.inputKeydown)
  },

  valueChangeAction() {
    if (this.choices) this.value = this.choices.getValue(true)
  },

  searchAction(e) {
    if (!this.createConfig) return

    const { createEvent, createText, createParams, tagsDomId } =
      this.createConfig
    this.searchValue = e.detail.value

    // Update the tag creation button text dynamically
    const tagsEl = document.getElementById(tagsDomId)
    if (tagsEl) {
      const searchValue = this.searchValue
      this.createButton = document.createElement(`button`)
      this.createButton.onclick = () =>
        this.createTag(createEvent, searchValue, createParams)
      this.createButton.classList.add(`--tags`)
      this.createButton.type = `button`
      this.createButton.dataset.thisValue = searchValue
      this.createButton.innerHTML = createText.replace(
        `{{TEXT}}`,
        `<b>${searchValue}</b>`
      )
      tagsEl.replaceChildren(this.createButton)
    }
  },

  createTag(createEvent, name, createParams) {
    if (!this.createButton) return

    this.createButton.disabled = true

    this.pushEvent(createEvent, { name, ...createParams })
      .then((reply) => {
        if (reply.id) {
          this.choices.setChoices(
            [{ value: String(reply.id), label: reply.name, selected: true }],
            `value`,
            `label`,
            false
          )
          this.choices.clearInput()
          this.value = this.choices.getValue(true)
          this.el.dispatchEvent(new Event(`change`, { bubbles: true }))
        }
      })
      .finally(() => {
        if (this.createButton) {
          this.createButton.disabled = false
        }
      })
  },

  destroyChoices() {
    if (this.inputElement && this.inputKeydown)
      this.inputElement.removeEventListener(`keydown`, this.inputKeydown)
    this.inputElement = null
    this.inputKeydown = null
    this.createConfig = null

    if (this.choices) {
      this.choices.destroy()
      this.choices = null
    }
  },
})
